import type { AppDispatch } from '@appStore/store'
import type { Types as OData } from 'shared/odata'

import { apiRTK, Tag } from 'api/api-rtk'
import { automationApi } from 'api/automationApi'
import { header } from "../../api/utils/requestHeaders.ts";
import { appConnector } from 'shared/connector'
import { fetchViaExtensionConditional } from 'shared/connector/rtk-adapt'

import type * as API from './types'

type PatchQualification = Pick<API.Contact, 'id' | 'qualification'>

const PAGE_ID = { id: 'Page', type: Tag.Contact }

export const api = apiRTK.injectEndpoints({
	endpoints: create => ({
		changeContactQualification: create.mutation<
			API.Contact,
			PatchQualification
		>({
			invalidatesTags: [PAGE_ID],
			query: ({ id, qualification }) => ({
				data: { value: qualification },
				method: 'PUT',
				url: `contacts/${id}/qualification`
			})
		}),
		contactById: create.query<API.Contact, string>({
			providesTags: (_r, _, id) => [{ id, type: Tag.Contact }],
			query: id => ({
				url: `contacts/${id}`
			})
		}),
		contactByLinkedInId: create.query<API.Contact | null, string>({
			providesTags: (_r, _, id) => [{ id, type: Tag.Contact }],
			query: id => ({
				params: {
					$filter: `linkedin_entity_id eq '${id}'`,
					$orderby: 'created desc',
					$take: 1
				},
				url: 'contacts/page'
			}),
			transformResponse: (response: API.ListResponse) =>
				response.documents[0] ?? null
		}),
		contactPage: create.query<API.ListResponse, Partial<OData.Page>>({
			async onQueryStarted(_, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled

					for (const contact of data.documents) {
						updateSingleContact(contact, dispatch)
					}
				} catch (_e) {
					return
				}
			},
			providesTags: [PAGE_ID],
			query: params => ({
				params,
				url: 'contacts/page'
			})
		}),
		contactsNote: create.query<API.NoteEntity, string>({
			query: id => ({
				url: `contacts/${id}/notes`
			})
		}),
		contactsNoteSave: create.mutation<
			API.Contact,
			API.NoteEntity & Pick<API.Contact, 'id'>
		>({
			async onQueryStarted({ id, value }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled
					const action = api.util.updateQueryData('contactsNote', id, () => ({
						value
					}))

					dispatch(action)
				} catch (_e) {
					return
				}
			},
			query: ({ id, value }) => ({
				data: { value },
				method: 'PUT',

				url: `contacts/${id}/notes`
			})
		}),
		deleteContacts: create.mutation<void, Partial<OData.AudienceParams>>({
			invalidatesTags: [Tag.Contact],
			query: params => ({
				method: 'DELETE',
				params,
				url: 'contacts'
			})
		}),
		export: create.query<string, Partial<OData.AudienceParams>>({
			query: params => ({
				params,
				url: 'contacts/export'
			})
		}),
		exportRecentActivities: create.query<string, Partial<OData.AudienceParams>>(
			{
				query: params => ({
					params,
					url: 'recent-activities/export'
				})
			}
		),
		locations: create.query<API.LocationItem[], Partial<OData.Page> | void>({
			query: params => ({
				params,
				url: 'contacts/locations'
			})
		}),

		saveContact: create.mutation<API.Contact, API.LinkedInFullContact>({
			invalidatesTags: [PAGE_ID],
			async onQueryStarted(_args, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					updateSingleContact(data, dispatch)
				} catch (_e) {
					return
				}
			},
			query: contact => ({
				data: contact,
				method: 'POST',
				url: 'contacts'
			})
		}),
		validateDeleteContacts: create.query<
			OData.ValidationPreview,
			Partial<OData.AudienceParams>
		>({
			query: params => ({
				params,
				url: 'contacts/delete/preview'
			})
		}),
		sendToTeamMember: create.mutation<void, API.SendToTeamMemberRequest>({
			invalidatesTags: [Tag.Contact, Tag.Imports],
			query: ({ personaId, correlationId, ...params }) => ({
				headers: header('dz-correlationId', correlationId),
				method: 'POST',
				params,
				url: `team/personas/${personaId}/contacts`
			})
		})
	}),

	overrideExisting: true
})

function updateSingleContact(contact: API.Contact, dispatch: AppDispatch) {
	let action = api.util.upsertQueryData(
		'contactByLinkedInId',
		contact.linkedin_entity_id,
		// @ts-ignore To invoke transformResponse
		{ documents: [contact], total: 1 }
	)
	dispatch(action)

	action = api.util.upsertQueryData('contactById', contact.id, contact)

	dispatch(action)
}

export const connectorApi = automationApi.injectEndpoints({
	endpoints: create => ({
		getLinkedInContact: create.query<API.LinkedInFullContact, string>({
			queryFn: fetchViaExtensionConditional<API.LinkedInFullContact, string>(
				id => ({
					fetchWithConnector() {
						return appConnector.linkedIn.contacts.getContactFullProfile(id)
					},
					method: 'GET',
					url: `contacts/${id}`
				})
			)
		})
	}),
	overrideExisting: true
})
